import React, { useEffect, useMemo, useState } from 'react';

import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import { fmt_count } from '../format';
import { fetch_preview_lines } from './api';
import { preview_lines_view, refusal } from './logic';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { PREVIEW_LINE_CSV_COLUMNS, preview_lines_csv_loader, rules_csv_name } from './csv';

/*
 * the preview's per-teacher list (GET /rules/preview/lines), biggest difference first; only
 * teacher-months with a difference have a row.
 */
const PAGE = 50;

const LINE_COLUMNS = [
  { key: 'where', label: 'Class' },
  { key: 'label', label: 'Line' },
  { key: 'old_text', label: 'Amount before', align: 'right' },
  { key: 'new_text', label: 'Amount after', align: 'right' }
];

const PreviewLines = ({ preview_uuid, catalogue }) => {
  // Wire rows, so a late catalogue relabels them without a re-fetch.
  const [raw, setRaw] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading'); // loading | ready | error | more
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState('');
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus(offset ? 'more' : 'loading');
    setError('');
    fetch_preview_lines(preview_uuid, PAGE, offset)
      .then((data) => {
        if (cancelled) return;
        const page = Array.isArray(data && data.rows) ? data.rows : [];
        setRaw((r) => (offset ? [...r, ...page] : page));
        setTotal(preview_lines_view(data).total);
        setStatus('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(refusal(e, 'Could not load the teachers in this preview.').text);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [preview_uuid, offset, rev]);

  const rows = useMemo(() => preview_lines_view({ rows: raw }, catalogue).rows, [raw, catalogue]);

  const columns = [
    { key: 'teacher_name', label: 'Teacher', hideInCard: true },
    { key: 'month', label: 'Month' },
    { key: 'old_text', label: 'Payable before', align: 'right' },
    { key: 'new_text', label: 'Payable after', align: 'right' },
    {
      key: 'delta_text',
      label: 'Change',
      align: 'right',
      render: (r) => <span className={r.delta < 0 ? 'tp-neg' : 'tp-pos'}>{r.delta_text}</span>
    },
    {
      key: 'lines',
      label: 'Lines',
      render: (r) => (
        <button
          type="button"
          className="tp-link"
          aria-expanded={open === r.key}
          onClick={() => setOpen((k) => (k === r.key ? '' : r.key))}
        >
          {open === r.key
            ? 'Hide lines'
            : `Show ${r.lines.length} line${r.lines.length === 1 ? '' : 's'}`}
        </button>
      )
    }
  ];
  const opened = rows.find((r) => r.key === open);

  return (
    <section className="tp-card" aria-labelledby="tp-rx-lines">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-rx-lines">
          Teachers whose pay changes
        </span>
        {status === 'ready' || status === 'more' ? (
          <span className="tp-card-hd-sub">
            {fmt_count(rows.length)} of {fmt_count(total)} shown
          </span>
        ) : null}
        <span className="tp-card-hd-acts">
          <DownloadCsvButton
            columns={PREVIEW_LINE_CSV_COLUMNS}
            load_rows={preview_lines_csv_loader(fetch_preview_lines, preview_uuid, catalogue)}
            file_name={() => rules_csv_name('preview-teachers')}
            disabled={status === 'loading' || (status === 'error' && !rows.length)}
          />
        </span>
      </div>
      {status === 'loading' ? (
        <div className="tp-empty" role="status">
          Loading teachers…
        </div>
      ) : null}
      {status === 'error' ? (
        <LoadFailed text={error} on_retry={() => setRev((r) => r + 1)} />
      ) : null}
      {status !== 'loading' && (status !== 'error' || rows.length) ? (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.key}
          cardTitle={(r) => <span className="tp-strong">{r.teacher_name}</span>}
          emptyText="No teacher's pay changes under this preview."
        />
      ) : null}
      {opened ? (
        <div style={{ marginTop: 12 }}>
          <div className="tp-strong" style={{ marginBottom: 6 }}>
            {opened.teacher_name}, {opened.month}: changed lines
          </div>
          <DataTable
            columns={LINE_COLUMNS}
            rows={opened.lines}
            rowKey={(l) => l.key}
            cardTitle={(l) => l.label}
            emptyText="No line-level detail for this teacher-month."
          />
        </div>
      ) : null}
      {status === 'ready' && rows.length < total ? (
        <button
          type="button"
          className="tp-btn-out"
          style={{ marginTop: 12 }}
          onClick={() => setOffset(rows.length)}
        >
          Show more teachers
        </button>
      ) : null}
      {status === 'more' ? (
        <div className="tp-hint" role="status">
          Loading more…
        </div>
      ) : null}
    </section>
  );
};

export default PreviewLines;
